import { useEffect, useState } from 'react';
import { apiFetch } from '../apiClient';

// Revenue view of the planning grids: hours x the Services billing rate in effect that month.
// A rate applies from the month its effectiveDate falls in. Hours before a project's first rate
// are priced at that first rate rather than dropped; a project with no rate prices at 0 and is
// counted so the page can say so.
const idx = (year, month) => year * 12 + (month - 1);

// Kinds (Judy, 2026-11-02): Provisional is a temp $/hr used only while a project has no Services
// rate; Ratable is a flat $/month (Recurring IT) that replaces hours x rate from its effective month.
export function buildRateIndex(rows) {
  const group = (kind) => {
    const m = new Map();
    for (const r of rows.filter((x) => (x.kind || 'Services') === kind)) {
      const d = new Date(r.effectiveDate);
      const list = m.get(r.projectId) || [];
      list.push({ i: idx(d.getUTCFullYear(), d.getUTCMonth() + 1), rate: Number(r.rate) });
      m.set(r.projectId, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.i - b.i);
    return m;
  };
  const byProject = group('Services');
  for (const [pid, list] of group('Provisional')) if (!byProject.has(pid)) byProject.set(pid, list);
  byProject.ratable = group('Ratable');
  return byProject;
}

/** Flat ratable $ for a project in month index `i`, or null when none is in effect (no back-fill). */
export function ratableAt(index, projectId, i) {
  let amt = null;
  for (const r of index?.ratable?.get(projectId) || []) { if (r.i <= i) amt = r.rate; else break; }
  return amt > 0 ? amt : null; // a 0 row ends the fee
}

/** True when a project has any way to be priced (an hourly or a ratable rate). */
export const isPriced = (index, projectId) => !!(index?.get(projectId)?.length || index?.ratable?.get(projectId)?.length);

/** $/hr for a project in month index `i` (year*12 + month-1); 0 when the project has no rate or is ratable that month. */
export function rateAt(index, projectId, i) {
  if (ratableAt(index, projectId, i) != null) return 0;
  const list = index?.get(projectId);
  if (!list?.length) return 0;
  let rate = list[0].rate;
  for (const r of list) { if (r.i <= i) rate = r.rate; else break; }
  return rate;
}

/** Null until loaded; an empty index (not null) if the fetch fails, so the grids still render. */
export function useRateIndex(enabled = true) {
  const [index, setIndex] = useState(null);
  useEffect(() => {
    if (!enabled || index) return;
    apiFetch('/capacity-planner/rates').then((rows) => setIndex(buildRateIndex(rows))).catch(() => setIndex(new Map()));
  }, [enabled, index]);
  return index;
}

export const money = (n) => `$${Math.round(n).toLocaleString()}`;
export const moneyCompact = (n) => {
  const a = Math.abs(n);
  const s = a >= 1e6 ? `${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(a / 1e3).toFixed(0)}k` : String(Math.round(a));
  return `${n < 0 ? '−' : ''}$${s}`;
};

export const PICKUP_NOTE = 'Pickup: budget left after forecast, parked in one month. Totals show hours x rate and pickup on separate lines.';

export const METRICS = [['hours', 'Hours'], ['revenue', 'Revenue']];

/** All projects' post-go-live pickups (hours, parked in one month each); null until loaded. */
export function usePickups(enabled = true) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!enabled || rows) return;
    apiFetch('/capacity-planner/pickups').then(setRows).catch(() => setRows([]));
  }, [enabled, rows]);
  return rows;
}

/** Pickup hours priced at the rate for the pickup month: [{ projectId, year, month, hours, revenue }]. */
export const pricePickups = (pickups, rateIndex) => (pickups || []).map((p) => ({ ...p, revenue: p.hours * rateAt(rateIndex, p.projectId, p.year * 12 + (p.month - 1)) }));
